import { useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Box, Button, Link, Typography } from '@mui/material';
import type { INotification } from '../models/Notification';
import moment from 'moment';
import { NotificationType } from '../enums/NotificationType';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';

interface NotificationItemProps {
  notification: INotification;
  setIsReadNotification: (notification: INotification, isRead: boolean) => void;
}

/** Delay before switching caption to exact date — avoids flicker when cursor moves over the line. */
const DATE_DETAIL_HOVER_MS = 420;

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  setIsReadNotification,
}) => {
  const isRead = !!notification.IsRead;
  const targetUrl = notification.OpenLink || notification.RelatedUrl;
  const [showExactDate, setShowExactDate] = useState(false);
  const dateHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDateHoverTimeout = () => {
    if (dateHoverTimeoutRef.current != null) {
      clearTimeout(dateHoverTimeoutRef.current);
      dateHoverTimeoutRef.current = null;
    }
  };

  useEffect(() => () => clearDateHoverTimeout(), []);

  const icon = useMemo(() => {
    return notification.Type === NotificationType.Message ? (
      <EditIcon fontSize="small" />
    ) : (
      <EventIcon fontSize="small" />
    );
  }, [notification.Type]);

  const handleReadToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReadNotification(notification, !isRead);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (!isRead) {
      setIsReadNotification(notification, true);
    }
    if (targetUrl) {
      window.location.assign(targetUrl);
    }
  };

  const dateLabel = useMemo(() => {
    const date = new Date(notification.CreatedAt);
    return showExactDate ? moment(date).format('MM/DD/YYYY h:mm:ss') : moment(date).fromNow();
  }, [notification.CreatedAt, showExactDate]);

  const handleDateMouseEnter = () => {
    clearDateHoverTimeout();
    dateHoverTimeoutRef.current = setTimeout(() => {
      dateHoverTimeoutRef.current = null;
      setShowExactDate(true);
    }, DATE_DETAIL_HOVER_MS);
  };

  const handleDateMouseLeave = () => {
    clearDateHoverTimeout();
    setShowExactDate(false);
  };

  return (
    <Box
      component={Link}
      href={targetUrl || undefined}
      onClick={handleClick}
      sx={{
        p: 1.75,
        width: '100%',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        bgcolor: isRead
          ? 'transparent'
          : (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.2)
                : alpha(theme.palette.primary.main, 0.06),
        '&:hover': {
          bgcolor: isRead
            ? (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.04)
            : (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.28)
                  : alpha(theme.palette.primary.main, 0.09),
        },
      }}
    >
      <Box
        sx={{
          mt: 0.25,
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(25, 118, 210, 0.1)',
          color: 'primary.main',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: isRead ? 500 : 650,
            mb: 0.5,
            lineHeight: 1.35,
            color: isRead ? 'text.secondary' : 'text.primary',
          }}
        >
          {notification.Title}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            '& *': {
              color: 'inherit !important',
            },
          }}
          dangerouslySetInnerHTML={{ __html: notification.SubTitle || '' }}
        />
        <Typography
          variant="caption"
          color="textSecondary"
          onMouseEnter={handleDateMouseEnter}
          onMouseLeave={handleDateMouseLeave}
          sx={{ display: 'block' }}
        >
          {dateLabel}
        </Typography>
      </Box>
      <Button
        variant="text"
        size="small"
        disableRipple
        onClick={handleReadToggle}
        sx={{
          minWidth: 32,
          p: 0.5,
          flexShrink: 0,
          alignSelf: 'flex-start',
          mt: 0.25,
          borderRadius: '50%',
          bgcolor: 'transparent',
          '&:hover': { bgcolor: 'transparent' },
          '&.Mui-focusVisible': { bgcolor: 'transparent' },
        }}
        title={isRead ? 'Mark as unread' : 'Mark as read'}
        aria-label={isRead ? 'Mark as unread' : 'Mark as read'}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            flexShrink: 0,
            ...(isRead
              ? {
                  bgcolor: 'background.paper',
                  border: '1.5px solid',
                  borderColor: 'divider',
                }
              : {
                  bgcolor: 'primary.main',
                  border: '1.5px solid',
                  borderColor: 'primary.dark',
                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
                }),
          }}
        />
      </Button>
    </Box>
  );
};

export default NotificationItem;
